<template>
  <div class="translation-container">
    <AppSectionHero
      title="Translation Management"
      subtitle="Manage translation memory, glossary, and review flagged translations."
      :stats="heroStats"
      :show-refresh="false"
    />

    <CCard class="border-0 shadow-sm filter-shell">
      <CCardBody class="p-3 p-lg-4">
        <div class="tabs-container">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="selectedTab = tab.id"
            :class="['tab-btn', { active: selectedTab === tab.id }]"
          >
            {{ tab.name }}
            <span v-if="tab.id === 'flagged' && flaggedTranslations.length" class="tab-badge">
              {{ flaggedTranslations.length }}
            </span>
          </button>
        </div>
      </CCardBody>
    </CCard>

    <div v-if="loadError" class="example-banner example-banner--error">
      {{ loadError }}
    </div>

    <div v-if="selectedTab === 'flagged'" class="tab-panel">
      <div class="panel-header">
        <h2 class="panel-title">Translation Documents</h2>
      </div>

      <div v-if="isLoading" class="empty-state">
        <div class="empty-icon">...</div>
        <h3 class="empty-title">Loading translation documents</h3>
        <p class="empty-message">Please wait while the live translation queue is refreshed.</p>
      </div>

      <div v-else-if="filteredFlagged.length === 0" class="empty-state">
        <div class="empty-icon">✓</div>
        <h3 class="empty-title">All caught up!</h3>
        <p class="empty-message">No translation documents are available right now.</p>
      </div>

      <div v-else class="flagged-list">
        <div v-for="item in paginatedFlagged" :key="item.id" class="flagged-item">
          <div class="item-header">
            <div class="item-badges">
              <span class="flag-badge" :class="item.flagType">
                {{ getFlagLabel(item.flagType) }}
              </span>
              <span class="language-badge">{{ item.sourceLang }} → {{ item.currentTargetLabel }}</span>
            </div>
            <span class="item-date">{{ formatDate(item.date) }}</span>
          </div>

          <div class="document-title">{{ item.documentTitle }}</div>
          <div class="document-meta">
            <span class="meta-pill">{{ item.documentType }}</span>
            <span class="meta-pill">Requested by {{ item.requestedBy }}</span>
            <span class="meta-pill">Translate to {{ item.currentTargetLabel }}</span>
          </div>

          <div class="document-workspace">
            <div class="preview-column">
              <div class="preview-label">Original Document</div>
              <div class="preview-surface">
                <p class="preview-text preview-document">{{ item.sourceText || 'No extracted source text yet.' }}</p>
              </div>
            </div>
            <div class="preview-column">
              <div class="preview-label">Translated Document ({{ item.currentTargetLabel }})</div>
              <div class="preview-surface">
                <p class="preview-text preview-document">{{ item.targetText || 'Translation not generated yet.' }}</p>
              </div>
              <div v-if="canDownloadTranslatedDocument(item)" class="download-actions">
                <button
                  v-if="canDownloadTranslatedDocx(item)"
                  class="download-link"
                  type="button"
                  @click="downloadTranslatedDocument(item, 'docx')"
                >
                  Download DOCX
                </button>
                <button
                  v-if="canDownloadTranslatedPdf(item)"
                  class="download-link"
                  type="button"
                  @click="downloadTranslatedDocument(item, 'pdf')"
                >
                  Download PDF
                </button>
              </div>
            </div>
          </div>

          <div class="item-actions">
            <template v-if="editingTranslationId === item.id">
              <div class="edit-area">
                <textarea v-model="editTranslationForm.targetText" rows="3" class="edit-input" />
                <div class="edit-actions">
                  <button class="action-btn secondary" @click="cancelTranslationEdit">Cancel</button>
                  <button class="action-btn primary" @click="saveTranslationEdit(item)">Save Changes</button>
                </div>
              </div>
            </template>
            <template v-else>
              <select v-model="translationSelections[item.id]" class="language-select">
                <option
                  v-for="option in languageOptions"
                  :key="option.value"
                  :value="option.value"
                  :disabled="option.value === normalizeLanguageValue(item.sourceLang)"
                >
                  {{ option.label }}
                </option>
              </select>
              <button class="action-btn primary" :disabled="!!translatingIds[item.id] || isSameLanguageSelection(item)" @click="translateDocument(item)">
                {{ translatingIds[item.id] ? 'Translating...' : 'Translate' }}
              </button>
              <button class="action-btn edit" @click="startEditTranslation(item)">
                View/Edit
              </button>
            </template>
          </div>
          <p v-if="isSameLanguageSelection(item)" class="translation-hint">
            Choose a target language different from the original document language.
          </p>
          <p v-if="translationErrors[item.id]" class="translation-error">
            {{ translationErrors[item.id] }}
          </p>
        </div>
      </div>

      <div v-if="filteredFlagged.length > pageSize" class="pagination">
        <div class="pagination-info">
          Showing {{ flaggedPaginationStart }} to {{ flaggedPaginationEnd }} of {{ filteredFlagged.length }} items
        </div>
        <div class="pagination-controls">
          <button v-if="flaggedPage > 1" class="page-btn" @click="flaggedPage--">
            ← Previous
          </button>
          <span class="page-indicator">Page {{ flaggedPage }} of {{ flaggedTotalPages }}</span>
          <button class="page-btn" :disabled="flaggedPage === flaggedTotalPages" @click="flaggedPage++">
            Next →
          </button>
        </div>
      </div>
    </div>

    <div v-if="selectedTab === 'memory'" class="tab-panel">
      <div class="panel-header">
        <h2 class="panel-title">Translation Memory</h2>
        <div class="panel-actions">
          <button class="primary-btn" @click="openAddMemoryModal">+ Add Entry</button>
        </div>
      </div>

      <div class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th @click="sortMemory('sourceText')" class="sortable">
                Source Text (EN) <span class="sort-icon">{{ getMemorySortIcon('sourceText') }}</span>
              </th>
              <th @click="sortMemory('targetText')" class="sortable">
                Translated Text <span class="sort-icon">{{ getMemorySortIcon('targetText') }}</span>
              </th>
              <th @click="sortMemory('language')" class="sortable">
                Language <span class="sort-icon">{{ getMemorySortIcon('language') }}</span>
              </th>
              <th @click="sortMemory('usageCount')" class="sortable">
                Usage <span class="sort-icon">{{ getMemorySortIcon('usageCount') }}</span>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in paginatedMemory" :key="entry.id" class="clickable-row" @click="viewMemoryEntry(entry)">
              <td class="source-cell">{{ entry.sourceText }}</td>
              <td class="target-cell">{{ entry.targetText }}</td>
              <td><span class="lang-tag">{{ entry.language }}</span></td>
              <td class="usage-cell">{{ entry.usageCount }} uses</td>
              <td class="actions-cell" @click.stop>
                <button class="icon-btn edit" @click="editMemory(entry)" title="Edit">✏️</button>
                <button class="icon-btn delete" @click="deleteMemory(entry)" title="Delete">🗑️</button>
              </td>
            </tr>
            <tr v-if="filteredMemory.length === 0">
              <td colspan="5" class="empty-table">
                <p>No memory entries found</p>
                <button class="add-first-btn" @click="openAddMemoryModal">+ Add Your First Entry</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="selectedTab === 'glossary'" class="tab-panel">
      <div class="panel-header">
        <h2 class="panel-title">Terminology Glossary</h2>
        <div class="panel-actions">
          <button class="primary-btn" @click="openAddTermModal">+ Add Term</button>
        </div>
      </div>

      <div class="table-container">
        <table class="data-table glossary-table">
          <thead>
            <tr>
              <th @click="sortGlossary('term')" class="sortable">English Term <span class="sort-icon">{{ getGlossarySortIcon('term') }}</span></th>
              <th @click="sortGlossary('thai')" class="sortable">Thai <span class="sort-icon">{{ getGlossarySortIcon('thai') }}</span></th>
              <th @click="sortGlossary('chinese')" class="sortable">Chinese <span class="sort-icon">{{ getGlossarySortIcon('chinese') }}</span></th>
              <th @click="sortGlossary('japanese')" class="sortable">Japanese <span class="sort-icon">{{ getGlossarySortIcon('japanese') }}</span></th>
              <th @click="sortGlossary('korean')" class="sortable">Korean <span class="sort-icon">{{ getGlossarySortIcon('korean') }}</span></th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="term in paginatedGlossary" :key="term.id" class="clickable-row" @click="viewTerm(term)">
              <td><strong>{{ term.term }}</strong></td>
              <td>{{ term.thai || '—' }}</td>
              <td>{{ term.chinese || '—' }}</td>
              <td>{{ term.japanese || '—' }}</td>
              <td>{{ term.korean || '—' }}</td>
              <td class="actions-cell" @click.stop>
                <button class="icon-btn edit" @click="editTerm(term)" title="Edit">✏️</button>
                <button class="icon-btn delete" @click="deleteTerm(term)" title="Delete">🗑️</button>
              </td>
            </tr>
            <tr v-if="filteredGlossary.length === 0">
              <td colspan="6" class="empty-table">
                <p>No glossary terms found</p>
                <button class="add-first-btn" @click="openAddTermModal">+ Add Your First Term</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="selectedTab === 'quality'" class="tab-panel">
      <div class="panel-header">
        <h2 class="panel-title">Quality Reports</h2>
        <div class="panel-actions">
          <button class="export-btn" @click="exportQualityReport">Export Report</button>
        </div>
      </div>

      <div class="quality-dashboard">
        <div class="quality-stats">
          <div class="quality-card">
            <div class="quality-label">Overall Quality Score</div>
            <div class="quality-value">{{ qualityScore }}%</div>
            <div class="quality-trend positive">Live score from current queue</div>
          </div>
          <div class="quality-card">
            <div class="quality-label">Total Translations</div>
            <div class="quality-value">{{ qualityData.volume.total }}</div>
            <div class="quality-detail">{{ qualityData.volume.auto }} auto • {{ qualityData.volume.edited }} edited</div>
          </div>
          <div class="quality-card">
            <div class="quality-label">Flagged Rate</div>
            <div class="quality-value">{{ flaggedRate }}%</div>
            <div class="quality-detail">{{ qualityData.volume.flagged }} flagged</div>
          </div>
        </div>

        <div class="quality-section">
          <h3 class="section-title">Quality by Language</h3>
          <div class="language-quality-list">
            <div v-for="lang in qualityData.byLanguage" :key="lang.name" class="language-quality-item">
              <span class="language-name">{{ lang.name }}</span>
              <div class="progress-bar-container">
                <div class="progress-bar" :style="{ width: lang.score + '%' }" />
              </div>
              <span class="language-score">{{ lang.score }}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showMemoryModal" class="modal-overlay" @click.self="closeMemoryModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">{{ editingMemory ? 'Edit' : 'Add' }} Translation Memory Entry</h2>
          <button class="close-btn" @click="closeMemoryModal">✕</button>
        </div>
        <form class="modal-form" @submit.prevent="saveMemoryEntry">
          <div class="form-group">
            <label class="form-label">Source Text (English)</label>
            <textarea v-model="memoryForm.sourceText" rows="2" required class="form-input" />
          </div>
          <div class="form-group">
            <label class="form-label">Translated Text</label>
            <textarea v-model="memoryForm.targetText" rows="2" required class="form-input" />
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Language</label>
              <select v-model="memoryForm.language" required class="form-select">
                <option value="">Select language</option>
                <option value="Thai">Thai</option>
                <option value="Chinese">Chinese</option>
                <option value="Japanese">Japanese</option>
                <option value="Korean">Korean</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">Category</label>
              <select v-model="memoryForm.category" class="form-select">
                <option value="General">General</option>
                <option value="Legal">Legal</option>
                <option value="Academic">Academic</option>
                <option value="Technical">Technical</option>
              </select>
            </div>
          </div>
          <div class="form-actions">
            <button type="button" class="cancel-btn" @click="closeMemoryModal">Cancel</button>
            <button type="submit" class="save-btn">{{ editingMemory ? 'Update Entry' : 'Save Entry' }}</button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="showTermModal" class="modal-overlay" @click.self="closeTermModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">{{ editingTerm ? 'Edit' : 'Add' }} Glossary Term</h2>
          <button class="close-btn" @click="closeTermModal">✕</button>
        </div>
        <form class="modal-form" @submit.prevent="saveTerm">
          <div class="form-group">
            <label class="form-label">English Term</label>
            <input v-model="termForm.term" required class="form-input" type="text">
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Thai</label>
              <input v-model="termForm.thai" class="form-input" type="text">
            </div>
            <div class="form-group">
              <label class="form-label">Chinese</label>
              <input v-model="termForm.chinese" class="form-input" type="text">
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Japanese</label>
              <input v-model="termForm.japanese" class="form-input" type="text">
            </div>
            <div class="form-group">
              <label class="form-label">Korean</label>
              <input v-model="termForm.korean" class="form-input" type="text">
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">Description</label>
            <textarea v-model="termForm.description" rows="2" class="form-input" />
          </div>
          <div class="form-actions">
            <button type="button" class="cancel-btn" @click="closeTermModal">Cancel</button>
            <button type="submit" class="save-btn">{{ editingTerm ? 'Update Term' : 'Save Term' }}</button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="showViewMemoryModal" class="modal-overlay" @click.self="closeViewMemoryModal">
      <div class="modal-content view-modal">
        <div class="modal-header">
          <h2 class="modal-title">Memory Entry Details</h2>
          <button class="close-btn" @click="closeViewMemoryModal">✕</button>
        </div>
        <div v-if="viewingMemory" class="modal-body">
          <div class="detail-item">
            <span class="detail-label">Source Text:</span>
            <div class="detail-content">{{ viewingMemory.sourceText }}</div>
          </div>
          <div class="detail-item">
            <span class="detail-label">Translated Text:</span>
            <div class="detail-content">{{ viewingMemory.targetText }}</div>
          </div>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">Language:</span>
              <span class="detail-value">{{ viewingMemory.language }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Category:</span>
              <span class="detail-value">{{ viewingMemory.category || 'General' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Usage Count:</span>
              <span class="detail-value">{{ viewingMemory.usageCount }} times</span>
            </div>
          </div>
          <div class="modal-actions">
            <button class="secondary-btn" @click="closeViewMemoryModal">Close</button>
            <button class="edit-btn" @click="editFromViewMemory">Edit</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showViewTermModal" class="modal-overlay" @click.self="closeViewTermModal">
      <div class="modal-content view-modal">
        <div class="modal-header">
          <h2 class="modal-title">Term Details</h2>
          <button class="close-btn" @click="closeViewTermModal">✕</button>
        </div>
        <div v-if="viewingTerm" class="modal-body">
          <div class="term-detail-header">
            <h3 class="term-title">{{ viewingTerm.term }}</h3>
          </div>
          <div class="translations-grid">
            <div class="translation-item"><span class="language-name">English:</span><span class="translation-text">{{ viewingTerm.term }}</span></div>
            <div v-if="viewingTerm.thai" class="translation-item"><span class="language-name">Thai:</span><span class="translation-text">{{ viewingTerm.thai }}</span></div>
            <div v-if="viewingTerm.chinese" class="translation-item"><span class="language-name">Chinese:</span><span class="translation-text">{{ viewingTerm.chinese }}</span></div>
            <div v-if="viewingTerm.japanese" class="translation-item"><span class="language-name">Japanese:</span><span class="translation-text">{{ viewingTerm.japanese }}</span></div>
            <div v-if="viewingTerm.korean" class="translation-item"><span class="language-name">Korean:</span><span class="translation-text">{{ viewingTerm.korean }}</span></div>
          </div>
          <div v-if="viewingTerm.description" class="detail-item">
            <span class="detail-label">Description:</span>
            <div class="detail-content">{{ viewingTerm.description }}</div>
          </div>
          <div class="modal-actions">
            <button class="secondary-btn" @click="closeViewTermModal">Close</button>
            <button class="edit-btn" @click="editFromViewTerm">Edit</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppSectionHero from '@/projects/components/layout/AppSectionHero'
import { canAutoRefresh } from '@/projects/utils/refresh-guard'
import api from '@/service/api'
import {
  downloadTextDocument,
  safeDocumentFileBase,
  triggerDocumentDownload
} from '@/utils/document-download'
import { showInfoAlert } from '@/utils/sweet-alert'
import { workflowNotificationEventName } from '@/utils/workflow-notifications'

const TRANSLATION_REFRESH_INTERVAL_MS = 30000

const LANGUAGE_OPTIONS = [
  { value: 'th', label: 'Thai' },
  { value: 'en', label: 'English' },
  { value: 'zh', label: 'Chinese' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' }
]

export default {
  name: 'AdminTranslations',
  components: {
    AppSectionHero
  },
  data() {
    return {
      selectedTab: 'flagged',
      flaggedSearch: '',
      memorySearch: '',
      glossarySearch: '',
      flaggedPage: 1,
      pageSize: 10,
      requests: [],
      isLoading: false,
      loadError: '',
      showMemoryModal: false,
      showTermModal: false,
      showViewMemoryModal: false,
      showViewTermModal: false,
      editingMemory: null,
      editingTerm: null,
      viewingMemory: null,
      viewingTerm: null,
      editingTranslationId: null,
      editTranslationForm: { targetText: '' },
      translationErrors: {},
      translationSelections: {},
      translatingIds: {},
      refreshTimer: null,
      memorySortField: 'sourceText',
      memorySortDirection: 'asc',
      glossarySortField: 'term',
      glossarySortDirection: 'asc',
      memoryForm: { sourceText: '', targetText: '', language: '', category: 'General' },
      termForm: { term: '', thai: '', chinese: '', japanese: '', korean: '', description: '' },
      tabs: [
        { id: 'flagged', name: 'All Documents' },
        { id: 'quality', name: 'Quality Reports' }
      ],
      translationMemory: [
        { id: 1, sourceText: 'Memorandum of Understanding', targetText: 'บันทึกความเข้าใจ', language: 'Thai', usageCount: 45, category: 'Legal' },
        { id: 2, sourceText: 'Academic Cooperation', targetText: '学术合作', language: 'Chinese', usageCount: 32, category: 'Academic' },
        { id: 3, sourceText: 'Intellectual Property', targetText: '知的財産', language: 'Japanese', usageCount: 28, category: 'Legal' }
      ],
      languageOptions: LANGUAGE_OPTIONS,
      glossary: [
        { id: 1, term: 'Memorandum of Understanding', thai: 'บันทึกความเข้าใจ', chinese: '谅解备忘录', japanese: '了解覚書', korean: '양해각서', description: 'Formal agreement between parties' },
        { id: 2, term: 'Academic Cooperation', thai: 'ความร่วมมือทางวิชาการ', chinese: '学术合作', japanese: '学術協力', korean: '학술 협력', description: 'Collaboration between institutions' }
      ],
      qualityData: {
        byLanguage: [
          { name: 'Thai', score: 95 },
          { name: 'Chinese', score: 87 },
          { name: 'Japanese', score: 82 },
          { name: 'Korean', score: 79 }
        ],
        volume: { total: 0, auto: 0, edited: 0, flagged: 0 }
      },
    }
  },
  computed: {
    heroStats() {
      return [
        { label: 'Needs Review', value: this.getFlaggedCountByType('requested'), hint: 'Waiting for admin translation', icon: 'cil-warning', iconClass: 'app-section-stat__icon--attention' },
        { label: 'Completed', value: this.getFlaggedCountByType('completed'), hint: 'Translated documents ready', icon: 'cil-check-circle', iconClass: 'app-section-stat__icon--active' },
        { label: 'Rejected', value: this.getFlaggedCountByType('rejected'), hint: 'Returned translation requests', icon: 'cil-x-circle', iconClass: 'app-section-stat__icon--danger' }
      ]
    },
    flaggedTranslations() {
      return this.requests
        .map(item => ({
          id: item._id || item.id,
          flagType: item.status || 'requested',
          date: item.update && item.update.datetime ? item.update.datetime : item.create && item.create.datetime,
          documentTitle: item.agreementTitle || 'Untitled agreement',
          documentType: item.agreementType || 'Agreement',
          partner: item.partner || '',
          country: item.country || '',
          partnerLogo: item.partnerLogo || null,
          requestedBy: item.requestedByName || 'Unknown requester',
          sourceLang: this.formatLanguageLabel(item.sourceLanguage || 'en'),
          targetLang: this.formatLanguageLabel(item.targetLanguage || 'th'),
          currentTargetLabel: this.formatLanguageLabel(this.translationSelections[item._id || item.id] || item.targetLanguage || 'th'),
          sourceText: item.sourceText || '',
          targetText: item.translatedText || '',
          outputAttachment: item.outputAttachment || null,
          raw: item
        }))
    },
    filteredFlagged() {
      let filtered = [...this.flaggedTranslations]
      if (this.flaggedSearch) {
        const q = this.flaggedSearch.toLowerCase()
        filtered = filtered.filter(i =>
          String(i.documentTitle).toLowerCase().includes(q) ||
          String(i.sourceText).toLowerCase().includes(q) ||
          String(i.targetText).toLowerCase().includes(q)
        )
      }
      return filtered
    },
    paginatedFlagged() {
      const start = (this.flaggedPage - 1) * this.pageSize
      return this.filteredFlagged.slice(start, start + this.pageSize)
    },
    flaggedTotalPages() {
      return Math.ceil(this.filteredFlagged.length / this.pageSize) || 1
    },
    flaggedPaginationStart() {
      return this.filteredFlagged.length === 0 ? 0 : ((this.flaggedPage - 1) * this.pageSize) + 1
    },
    flaggedPaginationEnd() {
      return Math.min(this.flaggedPage * this.pageSize, this.filteredFlagged.length)
    },
    filteredMemory() {
      let filtered = [...this.translationMemory]
      if (this.memorySearch) {
        const q = this.memorySearch.toLowerCase()
        filtered = filtered.filter(e =>
          e.sourceText.toLowerCase().includes(q) ||
          e.targetText.toLowerCase().includes(q)
        )
      }
      filtered.sort((a, b) => this.compareValues(a[this.memorySortField], b[this.memorySortField], this.memorySortDirection))
      return filtered
    },
    paginatedMemory() {
      return this.filteredMemory.slice(0, this.pageSize)
    },
    filteredGlossary() {
      let filtered = [...this.glossary]
      if (this.glossarySearch) {
        const q = this.glossarySearch.toLowerCase()
        filtered = filtered.filter(t =>
          (t.term && t.term.toLowerCase().includes(q)) ||
          (t.thai && t.thai.toLowerCase().includes(q)) ||
          (t.chinese && t.chinese.toLowerCase().includes(q)) ||
          (t.japanese && t.japanese.toLowerCase().includes(q)) ||
          (t.korean && t.korean.toLowerCase().includes(q))
        )
      }
      filtered.sort((a, b) => this.compareValues(a[this.glossarySortField], b[this.glossarySortField], this.glossarySortDirection))
      return filtered
    },
    paginatedGlossary() {
      return this.filteredGlossary.slice(0, this.pageSize)
    },
    qualityScore() {
      if (!this.qualityData.volume.total) return 87
      const base = 100 - Math.round((this.qualityData.volume.flagged / this.qualityData.volume.total) * 100)
      return Math.max(60, base)
    },
    flaggedRate() {
      if (!this.qualityData.volume.total) return '0.0'
      return ((this.qualityData.volume.flagged / this.qualityData.volume.total) * 100).toFixed(1)
    }
  },
  watch: {
    flaggedSearch() {
      this.flaggedPage = 1
    }
  },
  mounted() {
    this.fetchRequests({ forceRefresh: true })
    this.refreshTimer = window.setInterval(() => {
      if (canAutoRefresh()) this.fetchRequests({ forceRefresh: true, silent: true })
    }, TRANSLATION_REFRESH_INTERVAL_MS)
    window.addEventListener('focus', this.handleWindowFocus)
  },
  beforeDestroy() {
    if (this.refreshTimer) {
      window.clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }
    window.removeEventListener('focus', this.handleWindowFocus)
  },
  methods: {
    handleWindowFocus() {
      this.fetchRequests({ forceRefresh: true, silent: true })
    },
    notifyWorkflowChange() {
      window.dispatchEvent(new CustomEvent(workflowNotificationEventName(), {
        detail: { source: 'translation-requests' }
      }))
    },
    async fetchRequests(options = {}) {
      const silent = !!options.silent
      if (!silent) {
        this.isLoading = true
        this.loadError = ''
      }
      try {
        await this.$store.dispatch('auth/bootstrapSession')
        const response = await api.translation('requests', {}, {
          forceRefresh: !!options.forceRefresh
        })
        const liveRequests = Array.isArray(response && response.data && response.data.data)
          ? response.data.data
          : []
        this.requests = liveRequests
        this.loadError = ''
      } catch (error) {
        if (!silent) {
          this.requests = []
          this.loadError = error && error.response && error.response.data && error.response.data.data && error.response.data.data.message
            ? error.response.data.data.message
            : 'Unable to load translation requests. Please check your admin translation permission and backend translation service.'
        }
      } finally {
        if (!silent) {
          this.isLoading = false
        }
      }
      this.seedTranslationSelections()
      this.refreshQuality()
    },
    seedTranslationSelections() {
      const nextSelections = { ...this.translationSelections }
      this.requests.forEach((item) => {
        const id = item._id || item.id
        if (!nextSelections[id]) {
          nextSelections[id] = this.normalizeLanguageValue(item.targetLanguage || 'th')
        }
      })
      this.translationSelections = nextSelections
    },
    refreshQuality() {
      const total = this.requests.length
      const flagged = this.flaggedTranslations.length
      const auto = this.requests.filter(item => item.translatorEngine && item.translatorEngine !== 'pending').length
      const edited = this.requests.filter(item => item.status === 'completed' && item.update && item.update.datetime).length
      this.qualityData.volume = { total, auto, edited, flagged }
    },
    compareValues(aVal, bVal, direction) {
      let left = aVal || ''
      let right = bVal || ''
      if (typeof left === 'string') left = left.toLowerCase()
      if (typeof right === 'string') right = right.toLowerCase()
      if (left < right) return direction === 'asc' ? -1 : 1
      if (left > right) return direction === 'asc' ? 1 : -1
      return 0
    },
    formatDate(date) {
      return new Date(date || new Date()).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    },
    getFlaggedCountByType(type) {
      return this.flaggedTranslations.filter(f => f.flagType === type).length
    },
    getFlagLabel(flagType) {
      const labels = {
        requested: 'Requested',
        completed: 'Completed',
        rejected: 'Rejected'
      }
      return labels[flagType] || flagType
    },
    getNewThisWeek(type) {
      return type === 'memory' ? 2 : 1
    },
    normalizeLanguageValue(value) {
      const normalized = String(value || '').trim().toLowerCase()
      const map = {
        th: 'th',
        thai: 'th',
        en: 'en',
        english: 'en',
        zh: 'zh',
        chinese: 'zh',
        cn: 'zh',
        ja: 'ja',
        japanese: 'ja',
        jp: 'ja',
        ko: 'ko',
        korean: 'ko',
        kr: 'ko'
      }
      return map[normalized] || 'th'
    },
    formatLanguageLabel(value) {
      const map = {
        th: 'Thai',
        thai: 'Thai',
        en: 'English',
        english: 'English',
        zh: 'Chinese',
        chinese: 'Chinese',
        cn: 'Chinese',
        ja: 'Japanese',
        japanese: 'Japanese',
        jp: 'Japanese',
        ko: 'Korean',
        korean: 'Korean',
        kr: 'Korean'
      }
      return map[String(value || '').trim().toLowerCase()] || String(value || 'Thai')
    },
    isSameLanguageSelection(item) {
      return this.normalizeLanguageValue(this.translationSelections[item.id]) === this.normalizeLanguageValue(item.sourceLang)
    },
    isPdfAttachment(attachment) {
      const fileName = String(attachment && attachment.fileName || '').toLowerCase()
      const mimeType = String(attachment && attachment.mimeType || '').toLowerCase()
      const extension = String(attachment && attachment.extension || '').toLowerCase()
      const url = String(attachment && attachment.url || '').toLowerCase()
      return mimeType.includes('pdf')
        || extension === 'pdf'
        || fileName.endsWith('.pdf')
        || url.startsWith('data:application/pdf')
    },
    hasTranslatedTargetText(item) {
      return !!String(item && item.targetText ? item.targetText : '').trim()
    },
    canDownloadTranslatedDocx(item) {
      return !!(item && (this.hasTranslatedTargetText(item) || (item.outputAttachment && item.outputAttachment.url)))
    },
    canDownloadTranslatedPdf(item) {
      const attachment = item && item.outputAttachment ? item.outputAttachment : null
      return !!(item && (this.hasTranslatedTargetText(item) || (attachment && attachment.url && this.isPdfAttachment(attachment))))
    },
    canDownloadTranslatedDocument(item) {
      return this.canDownloadTranslatedDocx(item) || this.canDownloadTranslatedPdf(item)
    },
    async refreshTranslationItem(item) {
      await this.fetchRequests({ forceRefresh: true, silent: true })
      return this.flaggedTranslations.find(entry => String(entry.id) === String(item && item.id)) || item
    },
    translatedFileBase(item) {
      return `${safeDocumentFileBase(item && item.documentTitle ? item.documentTitle : 'translated-document')}-${safeDocumentFileBase(item && item.currentTargetLabel ? item.currentTargetLabel : 'translated')}`
    },
    async downloadTranslatedDocument(item, format = 'docx') {
      if (format === 'pdf' && !this.canDownloadTranslatedPdf(item)) return
      if (format !== 'pdf' && !this.canDownloadTranslatedDocx(item)) return
      let activeItem = item
      let attachment = activeItem && activeItem.outputAttachment ? activeItem.outputAttachment : null
      if (!this.hasTranslatedTargetText(activeItem) || !attachment || !attachment.url) {
        activeItem = await this.refreshTranslationItem(item)
        attachment = activeItem && activeItem.outputAttachment ? activeItem.outputAttachment : null
      }
      if (this.hasTranslatedTargetText(activeItem)) {
        return downloadTextDocument({
          title: `${activeItem.documentTitle || 'Translated Document'} - ${activeItem.currentTargetLabel || 'Translation'}`,
          subtitle: activeItem.currentTargetLabel ? `Translated to ${activeItem.currentTargetLabel}` : '',
          bodyText: activeItem.targetText,
          fileBase: this.translatedFileBase(activeItem),
          activityType: activeItem.documentType,
          partner: activeItem.partner || '',
          country: activeItem.country || '',
          partnerLogo: activeItem.partnerLogo || null,
          format
        })
      }
      if (attachment && attachment.url && (format !== 'pdf' || this.isPdfAttachment(attachment))) {
        return triggerDocumentDownload(
          attachment.url,
          attachment.fileName || `${this.translatedFileBase(activeItem)}.${format === 'pdf' ? 'pdf' : 'docx'}`
        )
      }
      return false
    },
    async translateDocument(item) {
      if (!(item && item.raw)) return
      const id = item.raw._id || item.raw.id
      const targetLanguage = this.normalizeLanguageValue(this.translationSelections[item.id] || item.raw.targetLanguage || 'th')
      if (this.isSameLanguageSelection(item)) return
      if (!String(item.sourceText || '').trim()) {
        this.$set(this.translationErrors, item.id, 'No source text is available for this request yet.')
        return
      }
      this.$delete(this.translationErrors, item.id)
      this.$set(this.translatingIds, item.id, true)
      try {
        await api.translation('run-translation', {
          id,
          sourceText: item.sourceText,
          sourceLanguage: this.normalizeLanguageValue(item.sourceLang),
          targetLanguage
        })
        await this.fetchRequests({ forceRefresh: true, silent: true })
        this.notifyWorkflowChange()
      } catch (error) {
        const message = error && error.response && error.response.data && error.response.data.data && error.response.data.data.message
          ? error.response.data.data.message
          : 'Translation failed. Please check the translation service configuration.'
        this.$set(this.translationErrors, item.id, message)
      } finally {
        this.$delete(this.translatingIds, item.id)
      }
    },
    startEditTranslation(item) {
      this.editingTranslationId = item.id
      this.editTranslationForm.targetText = item.targetText || ''
    },
    cancelTranslationEdit() {
      this.editingTranslationId = null
      this.editTranslationForm.targetText = ''
    },
    async saveTranslationEdit(item) {
      const request = this.requests.find(entry => String(entry._id || entry.id) === String(item.id))
      if (request) {
        request.translatedText = this.editTranslationForm.targetText
      }
      this.cancelTranslationEdit()
      this.refreshQuality()
    },
    sortMemory(field) {
      if (this.memorySortField === field) {
        this.memorySortDirection = this.memorySortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.memorySortField = field
        this.memorySortDirection = 'asc'
      }
    },
    getMemorySortIcon(field) {
      if (this.memorySortField !== field) return '↕'
      return this.memorySortDirection === 'asc' ? '↑' : '↓'
    },
    viewMemoryEntry(entry) {
      this.viewingMemory = entry
      this.showViewMemoryModal = true
    },
    closeViewMemoryModal() {
      this.showViewMemoryModal = false
      this.viewingMemory = null
    },
    editFromViewMemory() {
      this.editMemory(this.viewingMemory)
      this.closeViewMemoryModal()
    },
    openAddMemoryModal() {
      this.editingMemory = null
      this.memoryForm = { sourceText: '', targetText: '', language: '', category: 'General' }
      this.showMemoryModal = true
    },
    editMemory(entry) {
      this.editingMemory = entry
      this.memoryForm = { ...entry }
      this.showMemoryModal = true
    },
    closeMemoryModal() {
      this.showMemoryModal = false
      this.editingMemory = null
    },
    saveMemoryEntry() {
      if (this.editingMemory) {
        const idx = this.translationMemory.findIndex(m => m.id === this.editingMemory.id)
        if (idx !== -1) {
          this.translationMemory.splice(idx, 1, { ...this.editingMemory, ...this.memoryForm })
        }
      } else {
        this.translationMemory.push({
          id: Date.now(),
          ...this.memoryForm,
          usageCount: 0
        })
      }
      this.closeMemoryModal()
    },
    deleteMemory(entry) {
      this.translationMemory = this.translationMemory.filter(m => m.id !== entry.id)
    },
    sortGlossary(field) {
      if (this.glossarySortField === field) {
        this.glossarySortDirection = this.glossarySortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.glossarySortField = field
        this.glossarySortDirection = 'asc'
      }
    },
    getGlossarySortIcon(field) {
      if (this.glossarySortField !== field) return '↕'
      return this.glossarySortDirection === 'asc' ? '↑' : '↓'
    },
    viewTerm(term) {
      this.viewingTerm = term
      this.showViewTermModal = true
    },
    closeViewTermModal() {
      this.showViewTermModal = false
      this.viewingTerm = null
    },
    editFromViewTerm() {
      this.editTerm(this.viewingTerm)
      this.closeViewTermModal()
    },
    openAddTermModal() {
      this.editingTerm = null
      this.termForm = { term: '', thai: '', chinese: '', japanese: '', korean: '', description: '' }
      this.showTermModal = true
    },
    editTerm(term) {
      this.editingTerm = term
      this.termForm = { ...term }
      this.showTermModal = true
    },
    closeTermModal() {
      this.showTermModal = false
      this.editingTerm = null
    },
    saveTerm() {
      if (this.editingTerm) {
        const idx = this.glossary.findIndex(t => t.id === this.editingTerm.id)
        if (idx !== -1) {
          this.glossary.splice(idx, 1, { ...this.editingTerm, ...this.termForm })
        }
      } else {
        this.glossary.push({ id: Date.now(), ...this.termForm })
      }
      this.closeTermModal()
    },
    deleteTerm(term) {
      this.glossary = this.glossary.filter(t => t.id !== term.id)
    },
    exportQualityReport() {
      showInfoAlert('Quality report export can be connected next.', 'Coming Soon')
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-page-frame("translation-container");
@include workspace-toolbar-search;
@include workspace-button-system;

.translation-container {
  margin: 0 auto;
  max-width: 1400px;
  padding: 24px;
}

.filter-shell {
  border-radius: 18px;
  margin-bottom: 24px;
}

.stat-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
  cursor: pointer;
  padding: 20px;
  transition: all 0.2s;
}

.stat-card:hover {
  border-color: #9e212f;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  transform: translateY(-2px);
}

.stat-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.stat-label {
  color: #475569;
  font-size: 14px;
  font-weight: 500;
}

.stat-icon {
  align-items: center;
  border-radius: 10px;
  display: flex;
  font-size: 18px;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.red-bg { background: #fee2e2; color: #b91c1c; }
.beige-bg { background: #ffedd5; color: #9a3412; }
.green-bg { background: #dcfce7; color: #166534; }
.blue-bg { background: #f7e8eb; color: #8a1728; }

.stat-number {
  color: #0f172a;
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 8px;
}

.stat-footer {
  color: #64748b;
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  gap: 8px;
}

.stat-trend.positive {
  color: #166534;
}

.tabs-container {
  display: flex;
  gap: 8px;
}

.example-banner {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 12px;
  color: #9a3412;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 20px;
  padding: 12px 16px;
}

.tab-btn {
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  padding: 10px 20px;
  position: relative;
  transition: all 0.2s;
}

.tab-btn.active {
  color: #9e212f;
  font-weight: 600;
}

.tab-btn.active::after {
  background: #9e212f;
  bottom: -1px;
  content: '';
  height: 2px;
  left: 0;
  position: absolute;
  right: 0;
}

.tab-badge {
  background: #fee2e2;
  border-radius: 12px;
  color: #b91c1c;
  font-size: 11px;
  margin-left: 6px;
  padding: 2px 6px;
}

.tab-panel {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.02);
  padding: 24px;
}

.panel-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.panel-title,
.section-title {
  color: #0f172a;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.panel-actions {
  display: flex;
  gap: 12px;
}

.search-box {
  min-width: 240px;
}

.search-box.full-width {
  margin-bottom: 20px;
  width: 100%;
}

.search-box input::-webkit-search-decoration,
.search-box input::-webkit-search-cancel-button,
.search-box input::-webkit-search-results-button,
.search-box input::-webkit-search-results-decoration {
  -webkit-appearance: none;
  appearance: none;
  display: none;
}

.search-box input:focus,
.edit-input:focus,
.form-input:focus,
.form-select:focus {
  outline: none;
}

.flagged-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.flagged-item {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  transition: all 0.2s;
}

.flagged-item:hover {
  border-color: #9e212f;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.item-header {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 12px;
}

.item-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.flag-badge,
.language-badge,
.lang-tag {
  border-radius: 30px;
  display: inline-block;
  font-size: 12px;
  padding: 4px 12px;
}

.flag-badge.requested {
  background: #fef9c3;
  color: #854d0e;
}

.flag-badge.rejected {
  background: #fee2e2;
  color: #b91c1c;
}

.flag-badge.completed {
  background: #dcfce7;
  color: #166534;
}

.language-badge,
.lang-tag {
  background: #f1f5f9;
  color: #334155;
}

.item-date,
.pagination-info,
.page-indicator {
  color: #64748b;
  font-size: 12px;
}

.document-title {
  color: #0f172a;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
}

.document-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.meta-pill {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 999px;
  color: #9a3412;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 10px;
}

.document-workspace {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr 1fr;
  margin: 16px 0;
  padding: 16px;
}

.preview-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview-surface {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  min-height: 240px;
  padding: 16px;
}

.preview-label,
.detail-label {
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.preview-text,
.detail-value,
.detail-content,
.translation-text {
  color: #0f172a;
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
}

.preview-document {
  white-space: pre-wrap;
}

.download-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.download-link {
  align-self: flex-start;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 999px;
  color: #9a3412;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  margin-top: 0;
  padding: 8px 14px;
  text-decoration: none;
}

.download-link:hover {
  background: #ffedd5;
  text-decoration: none;
}

.translation-hint {
  color: #b45309;
  font-size: 12px;
  font-weight: 600;
  margin: 10px 0 0;
  text-align: right;
}

.translation-error {
  color: #b91c1c;
  font-size: 12px;
  font-weight: 600;
  margin: 8px 0 0;
  text-align: right;
}

.item-actions,
.edit-actions,
.form-actions,
.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.action-btn,
.primary-btn,
.export-btn,
.cancel-btn,
.save-btn,
.secondary-btn,
.edit-btn {
  border-radius: 12px;
  font-size: 13px;
  font-weight: 700;
  min-height: 42px;
  padding: 10px 16px;
}

.action-btn {
  border: 0;
}

.action-btn.approve { background: #9e212f; color: #fff; }
.action-btn.edit { background: #e5e7eb; color: #344767; }
.action-btn.reject { background: #dc2626; color: #fff; }
.edit-btn { background: #e5e7eb; border: 1px solid #d1d5db; color: #344767; }

.action-btn:disabled {
  background: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
  opacity: 1;
}

.language-select {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #0f172a;
  font-size: 13px;
  font-weight: 500;
  min-width: 140px;
  padding: 8px 12px;
}

.language-select:focus {
  border-color: #9e212f;
  box-shadow: 0 0 0 3px rgba(158, 33, 47, 0.12);
  outline: none;
}

.edit-area {
  width: 100%;
}

.edit-input,
.form-input,
.form-select {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #0f172a;
  font-family: inherit;
  font-size: 14px;
  padding: 12px;
  width: 100%;
}

.table-container {
  overflow-x: auto;
}

.data-table {
  border-collapse: collapse;
  font-size: 14px;
  width: 100%;
}

.data-table th,
.data-table td {
  border-bottom: 1px solid #f1f5f9;
  padding: 16px;
}

.data-table th {
  background: #f8fafc;
  color: #475569;
  font-weight: 600;
  text-align: left;
}

.clickable-row {
  cursor: pointer;
}

.source-cell,
.target-cell {
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.actions-cell {
  white-space: nowrap;
}

.icon-btn {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  height: 32px;
  margin: 0 2px;
  width: 32px;
}

.pagination {
  align-items: center;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  padding: 20px 0 12px;
}

.pagination-controls {
  align-items: center;
  display: flex;
  flex: 1;
  gap: 16px;
  justify-content: center;
  position: relative;
}

.page-btn {
  min-width: 112px;
}

.pagination-controls .page-btn:first-child {
  left: 0;
  position: absolute;
}

.pagination-controls .page-btn:last-child {
  position: absolute;
  right: 0;
}

.quality-dashboard {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.quality-stats {
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(3, 1fr);
}

.quality-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
}

.quality-label {
  color: #475569;
  font-size: 13px;
  margin-bottom: 8px;
}

.quality-value {
  color: #0f172a;
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 8px;
}

.quality-trend.positive,
.quality-detail {
  color: #64748b;
  font-size: 12px;
}

.language-quality-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.language-quality-item {
  align-items: center;
  display: flex;
  gap: 12px;
}

.language-name {
  color: #334155;
  width: 100px;
}

.progress-bar-container {
  background: #e2e8f0;
  border-radius: 4px;
  flex: 1;
  height: 8px;
  overflow: hidden;
}

.progress-bar {
  background: #9e212f;
  height: 100%;
}

.language-score {
  color: #0f172a;
  font-weight: 600;
  text-align: right;
  width: 48px;
}

.modal-overlay {
  align-items: center;
  backdrop-filter: blur(4px);
  background: rgba(15, 23, 42, 0.5);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 20px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  max-height: 90vh;
  max-width: 600px;
  overflow-y: auto;
  width: 90%;
}

.view-modal {
  max-width: 500px;
}

.modal-header {
  align-items: center;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  padding: 20px 28px;
}

.modal-title {
  color: #0f172a;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  font-size: 20px;
}

.modal-body,
.modal-form {
  padding: 28px;
}

.form-row,
.detail-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr 1fr;
}

.detail-content {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
}

.term-title {
  color: #0f172a;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.translations-grid {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 20px 0;
  padding: 16px;
}

.translation-item {
  display: flex;
  gap: 8px;
}

.empty-state,
.empty-table {
  color: #64748b;
  padding: 40px;
  text-align: center;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

@media (max-width: 1024px) {
  .translation-container {
    padding: 24px;
  }

  .quality-stats,
  .form-row,
  .detail-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .translation-container {
    padding: 20px;
  }

  .panel-header,
  .item-header,
  .pagination,
  .modal-actions,
  .item-actions {
    align-items: flex-start;
    flex-direction: column;
  }

  .document-workspace,
  .quality-stats,
  .form-row,
  .detail-grid {
    grid-template-columns: 1fr;
  }

  .pagination-controls {
    width: 100%;
  }

  .pagination-controls .page-btn:first-child,
  .pagination-controls .page-btn:last-child {
    position: static;
  }
}
</style>
